import React, { useState } from "react";
import {
  FaCreditCard,
  FaLock,
  FaCheckCircle,
  FaShieldAlt,
} from "react-icons/fa";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../config/firebase";

const BKTPayment = ({ bookingData, onSuccess, onError, createBooking }) => {
  const [processing, setProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const isTestMode = process.env.REACT_APP_PAYMENT_TEST_MODE === "true";

  const handleTestPayment = async () => {
    try {
      setProcessing(true);
      const result = await createBooking({ ...bookingData, userId: "user123" });
      // Mark as paid so the Firestore onBookingUpdated trigger fires and sends emails
      const { updateDoc, doc } = await import("firebase/firestore");
      const { db } = await import("../../config/firebase");
      await updateDoc(doc(db, "bookings", result.bookingId), {
        status: "confirmed",
        paymentStatus: "paid",
        paymentId: "TEST-" + Date.now(),
      });
      if (onSuccess)
        onSuccess({
          bookingId: result.bookingId,
          bookingReference: result.bookingReference,
          test: true,
        });
    } catch (error) {
      setProcessing(false);
      setErrorMessage(error.message || "Test booking failed.");
      if (onError) onError(error);
    }
  };

  const handleBKTPayment = async () => {
    try {
      setErrorMessage("");
      setProcessing(true);

      if (!bookingData.totalPrice || bookingData.totalPrice <= 0) {
        throw new Error("Invalid booking amount");
      }
      if (
        !bookingData.customerInfo?.email ||
        !(bookingData.customerInfo?.fullName || bookingData.customerInfo?.name)
      ) {
        throw new Error("Customer information is required");
      }

      // Step 1: Create booking in Firestore (status: pending, paymentStatus: pending)
      const result = await createBooking({ ...bookingData, userId: "user123" });
      const { bookingId } = result;

      // Step 2: Get signed form fields from server (Store Key never leaves Firebase Function)
      const callbackUrl = `${
        process.env.REACT_APP_FUNCTIONS_URL ||
        "https://us-central1-seapulse-albania.cloudfunctions.net"
      }/bktCallback`;
      const getBKTFormData = httpsCallable(functions, "getBKTFormData");
      // Send amount in EUR directly (BKT terminal currency: 978 = EUR)
      const amountInEUR = parseFloat(bookingData.totalPrice).toFixed(2);
      const { data: formFields } = await getBKTFormData({
        bookingId,
        amount: amountInEUR,
        okUrl: callbackUrl,
        failUrl: callbackUrl,
        lang: "en",
      });

      // Step 3: Build a hidden HTML form and submit to BKT 3D gate
      const bkt3dUrl = process.env.REACT_APP_BKT_3D_POST_URL;
      if (!bkt3dUrl) throw new Error("BKT 3D Post URL is not configured.");

      const form = document.createElement("form");
      form.method = "POST";
      form.action = bkt3dUrl;
      form.style.display = "none";
      Object.entries(formFields).forEach(([key, value]) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = key;
        input.value = String(value);
        form.appendChild(input);
      });
      document.body.appendChild(form);
      form.submit();
      // Browser navigates away — processing stays true intentionally
    } catch (error) {
      console.error("BKT payment error:", error);
      setProcessing(false);
      setErrorMessage(
        error.message || "Failed to initiate payment. Please try again.",
      );
      if (onError) onError(error);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Error Message */}
      {errorMessage && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-lg sm:rounded-xl p-3 sm:p-4">
          <div className="flex items-start gap-2 sm:gap-3">
            <div className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 bg-red-500 rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-xs sm:text-sm">!</span>
            </div>
            <div>
              <h4 className="font-semibold text-red-900 dark:text-red-200 mb-1 text-sm sm:text-base">
                Payment Error
              </h4>
              <p className="text-xs sm:text-sm text-red-800 dark:text-red-300">
                {errorMessage}
              </p>
              <button
                onClick={() => setErrorMessage("")}
                className="mt-2 text-xs sm:text-sm font-semibold text-red-700 dark:text-red-400 hover:text-red-900 dark:hover:text-red-200 underline"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* BKT Bank Payment Info */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg sm:rounded-xl p-4 sm:p-6">
        <div className="flex items-center gap-2 sm:gap-3 mb-4">
          <div className="w-9 h-9 sm:w-10 sm:h-10 border border-slate-200 rounded-lg flex items-center justify-center bg-slate-50">
            <FaCreditCard className="text-slate-500 text-sm" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              Complete Payment
            </h3>
            <p className="text-[10px] text-gray-500 dark:text-gray-400">
              You will be redirected to BKT Bank's secure page
            </p>
          </div>
        </div>

        {/* Payment Button — top of card */}
        {!isTestMode && !processing && !errorMessage && (
          <button
            onClick={handleBKTPayment}
            className="w-full bg-slate-900 hover:bg-slate-700 text-white font-semibold py-3 sm:py-3.5 px-4 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2 text-sm mb-4"
          >
            <FaLock className="text-sm" />
            <span>Proceed to BKT Bank Payment</span>
          </button>
        )}

        {/* Card info bullets */}
        <div className="space-y-2 mb-4">
          <div className="flex items-center gap-2 text-[11px] text-slate-500">
            <FaCheckCircle className="text-slate-300 flex-shrink-0" />
            <span>All major credit and debit cards accepted</span>
          </div>
          <div className="flex items-center gap-2 text-[11px] text-slate-500">
            <FaCheckCircle className="text-slate-300 flex-shrink-0" />
            <span>Visa, Mastercard, and local Albanian cards</span>
          </div>
          <div className="flex items-center gap-2 text-[11px] text-slate-500">
            <FaCheckCircle className="text-slate-300 flex-shrink-0" />
            <span>Secure 3D Secure authentication</span>
          </div>
          <div className="flex items-center gap-2 text-[11px] text-slate-500">
            <FaCheckCircle className="text-slate-300 flex-shrink-0" />
            <span>Instant booking confirmation</span>
          </div>
        </div>

        {/* Test Mode Button */}
        {isTestMode && (
          <div className="mt-3 p-3 bg-yellow-50 border border-yellow-300 rounded-lg">
            <p className="text-xs text-yellow-700 font-semibold mb-2">
              🧪 TEST MODE — No real payment will be charged
            </p>
            <button
              onClick={handleTestPayment}
              className="w-full bg-yellow-500 hover:bg-yellow-600 text-white font-semibold py-3 px-4 rounded-lg transition-all text-sm flex items-center justify-center gap-2"
            >
              <FaCheckCircle />
              Simulate Successful Payment
            </button>
          </div>
        )}

        {/* Processing State */}
        {processing && (
          <div className="bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-lg p-4 sm:p-6 text-center">
            <div className="flex flex-col items-center gap-3 sm:gap-4">
              <div className="relative">
                <div className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-4 border-gray-200 dark:border-gray-700 border-t-gray-600 dark:border-t-gray-400"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <FaCreditCard className="text-gray-600 dark:text-gray-400 text-lg sm:text-2xl" />
                </div>
              </div>
              <div>
                <p className="font-semibold text-gray-900 dark:text-white text-base sm:text-lg mb-1">
                  Redirecting to BKT Bank...
                </p>
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                  Please wait while we redirect you to secure payment page
                </p>
                <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Do not close this window
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Security Info */}
        <div className="flex items-start gap-1.5 sm:gap-2 bg-gray-50 dark:bg-gray-900/50 rounded-lg p-2.5 sm:p-4 mt-3 sm:mt-4">
          <FaLock className="text-gray-500 dark:text-gray-400 text-xs sm:text-sm mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs sm:text-sm text-gray-900 dark:text-white font-semibold mb-1 flex items-center gap-1.5 sm:gap-2">
              <FaShieldAlt className="text-xs sm:text-sm" />
              100% Secure Payment
            </p>
            <p className="text-[10px] sm:text-xs text-gray-600 dark:text-gray-400">
              You will be redirected to BKT Bank's secure payment gateway. Your
              card information is protected with bank-level encryption and 3D
              Secure authentication.
            </p>
          </div>
        </div>

        {/* Accepted Cards */}
        <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-blue-200 dark:border-blue-800">
          <p className="text-[10px] sm:text-xs text-center text-gray-600 dark:text-gray-400 mb-2">
            Accepted Payment Methods
          </p>
          <div className="flex items-center justify-center gap-2 sm:gap-4">
            <div className="bg-white dark:bg-gray-700 rounded px-1.5 sm:px-2 py-1 shadow-sm">
              <span className="text-[10px] sm:text-xs font-bold text-blue-600">
                VISA
              </span>
            </div>
            <div className="bg-white dark:bg-gray-700 rounded px-1.5 sm:px-2 py-1 shadow-sm">
              <span className="text-[10px] sm:text-xs font-bold text-red-600">
                Mastercard
              </span>
            </div>
            <div className="bg-white dark:bg-gray-700 rounded px-1.5 sm:px-2 py-1 shadow-sm">
              <span className="text-[10px] sm:text-xs font-bold text-gray-700 dark:text-gray-300">
                BKT
              </span>
            </div>
          </div>
        </div>

        {/* Company Info */}
        <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
          <p className="text-[10px] text-gray-400 dark:text-gray-500 text-center leading-relaxed">
            <span className="font-semibold text-gray-500 dark:text-gray-400">
              SeaPulse Albania
            </span>
            <br />
            Company Registration No. L81616503M
          </p>
        </div>
      </div>
    </div>
  );
};

export default BKTPayment;
